import { useState, useEffect, useMemo } from "react"
import { Icon } from "~/react/ui/icon"
import { Switch } from "~/react/ui/switch"
import { Separator } from "~/react/ui/separator"
import { Alert, AlertIcon, AlertContent, AlertDescription } from "~/react/ui/alert"
import { useCampaignContent } from "../campaign_content_context"
import { EmailEditorCard } from "./email_editor_card"
import { Distribution } from "./distribution"
import { t } from "../i18n"
import { cn } from "~/lib/utils"
import type { FollowUp } from "../types"

type AbTestAlertState =
  | "identical"
  | "testing_both"
  | "different_subjects"
  | "different_body"

const ALERT_CONFIG: Record<
  AbTestAlertState,
  {
    variant: "warning" | "success"
    icon: string
    translationKey: string
  }
> = {
  identical: {
    variant: "warning",
    icon: "exclamation-triangle",
    translationKey: "js.campaigns.content.ab_variants_identical_warning",
  },
  testing_both: {
    variant: "warning",
    icon: "exclamation-triangle",
    translationKey: "js.campaigns.content.ab_variants_test_both_warning",
  },
  different_subjects: {
    variant: "success",
    icon: "check-circle",
    translationKey: "js.campaigns.content.ab_variants_different_subjects",
  },
  different_body: {
    variant: "success",
    icon: "check-circle",
    translationKey: "js.campaigns.content.ab_variants_different_body",
  },
}

function getAbTestAlertState(
  variantA: FollowUp,
  variantB: FollowUp,
): AbTestAlertState {
  // TODO(dev, CAMPAIGNS_AB_TESTING): Implement actual logic to determine state
  // based on comparing variant A and variant B subject/body
  const subjectsMatch = variantA.subject === variantB.subject
  const bodiesMatch = variantA.body === variantB.body

  if (subjectsMatch && bodiesMatch) {
    return "identical"
  }

  if (!subjectsMatch && !bodiesMatch) {
    return "testing_both"
  }

  if (!subjectsMatch) {
    return "different_subjects"
  }

  return "different_body"
}

interface AbTestAlertProps {
  state: AbTestAlertState
}

function AbTestAlert({ state }: AbTestAlertProps) {
  const config = ALERT_CONFIG[state]
  const iconColorClass =
    config.variant === "warning" ? "text-warning" : "text-success"

  return (
    <Alert
      variant={config.variant}
      layout="compact"
      className="follow-up-editor__distribution-alert"
    >
      <AlertIcon>
        <Icon
          className={iconColorClass}
          icon={config.icon}
          aria-hidden="true"
        />
      </AlertIcon>
      <AlertContent>
        <AlertDescription
          dangerouslySetInnerHTML={{
            __html: t(config.translationKey),
          }}
        />
      </AlertContent>
    </Alert>
  )
}

export function FollowUpEditor() {
  const { followUps, selectedStep, editable, updateFollowUp } = useCampaignContent()

  const currentFollowUp = followUps.find((fu) => fu.step === selectedStep)

  // TODO(dev, CAMPAIGNS_AB_TESTING): Temporarily disconnected - Variant B uses a local copy
  // that doesn't trigger updates. This allows testing the UI independently. Once backend
  // support is added, Variant B should be connected to its own followUp data.
  const [variantBFollowUp, setVariantBFollowUp] = useState<FollowUp | null>(
    currentFollowUp ? { ...currentFollowUp } : null,
  )

  useEffect(() => {
    if (currentFollowUp) {
      setVariantBFollowUp({ ...currentFollowUp })
    }
  }, [currentFollowUp?.id])

  if (!currentFollowUp) {
    return null
  }

  const isOpeningEmail = currentFollowUp.step === 0
  const isAbTestEnabled = currentFollowUp?.ab_testing_enabled ?? false

  const handleAbTestToggle = (checked: boolean) => {
    if (currentFollowUp) {
      updateFollowUp(currentFollowUp.step, { ab_testing_enabled: checked })
    }
  }

  const abTestAlertState = useMemo<AbTestAlertState | null>(() => {
    if (!isAbTestEnabled || !variantBFollowUp) {
      return null
    }
    return getAbTestAlertState(currentFollowUp, variantBFollowUp)
  }, [isAbTestEnabled, currentFollowUp, variantBFollowUp])

  return (
    <section
      id="campaign-step-editor"
      aria-label="Step editor"
      className={cn("follow-up-editor", isAbTestEnabled && "follow-up-editor--ab")}
    >
      <header className="follow-up-editor__header-card">
        <div className="follow-up-editor__header-top">
          <div className="follow-up-editor__header-left">
            <Icon icon="envelope" className="follow-up-editor__icon" size="sm" />
            <h2 className="follow-up-editor__title">
              {isOpeningEmail
                ? t("js.campaigns.content.opening_email")
                : t("js.campaigns.content.follow_up", {
                    step: currentFollowUp.step,
                  })}
            </h2>
          </div>

          <div className="follow-up-editor__header-right">
            <span className="follow-up-editor__ab-toggle-label">
              {t("js.campaigns.content.ab_test_toggle")}
            </span>
            <Switch
              checked={isAbTestEnabled}
              onCheckedChange={handleAbTestToggle}
              disabled={!editable}
            />
          </div>
        </div>

        {isAbTestEnabled && abTestAlertState && (
          <>
            <Separator orientation="horizontal" className="follow-up-editor__separator" />
            <div className="follow-up-editor__distribution">
              <AbTestAlert state={abTestAlertState} />
              <Distribution />
            </div>
          </>
        )}
      </header>

      <div className={cn(isAbTestEnabled && "follow-up-editor__variants")}>
        <div className={cn(isAbTestEnabled && "follow-up-editor__variant")}>
          <EmailEditorCard
            key={`${currentFollowUp.id}-a`}
            followUp={currentFollowUp}
            isAbVariant={isAbTestEnabled}
            variantTagLabel={isAbTestEnabled ? t("js.campaigns.content.ab_variant_a") : undefined}
          />
        </div>

        {isAbTestEnabled && (
          <div className="follow-up-editor__variant">
            <EmailEditorCard
              key={`${currentFollowUp.id}-b`}
              followUp={variantBFollowUp || currentFollowUp}
              isAbVariant={true}
              variantTagLabel={t("js.campaigns.content.ab_variant_b")}
              isLocal={true}
            />
          </div>
        )}
      </div>
    </section>
  )
}
